import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Container } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import 'dayjs/locale/it';

import getTheme from './theme/theme';
import getConfig from './config/app-config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminProvider } from './contexts/AdminContext';
import { TeacherProvider } from './contexts/TeacherContext';
import ProtectedRoute from './components/ProtectedRoute';
import DynamicFavicon from './components/DynamicFavicon';
import OAuthCallbackHandler from './components/OAuthCallbackHandler';

// import FloatingAdmin from './components/FloatingAdmin';

import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import VerifyEmail from './pages/auth/VerifyEmail';
import ResetPassword from './pages/auth/ResetPassword';
import ForgotPassword from './pages/auth/ForgotPassword';
import RegistrationSuccess from './pages/auth/RegistrationSuccess';

import MemberProfile from './pages/member/MemberProfile';
import MemberCalendar from './pages/member/MemberCalendar';
import TrialLessons from './pages/member/TrialLessons';
import MembershipCheckout from './pages/membership-workflow/MembershipCheckout';
import MembershipCallback from './pages/membership-workflow/MembershipCallback';
import Courses from './pages/courses/Courses';
import MyCourses from './pages/courses/MyCourses';
import CourseEnrollmentCheckout from './pages/courses/CourseEnrollmentCheckout';
import CourseEnrollmentCallback from './pages/courses/CourseEnrollmentCallback';
import CourseUpkeepCheckout from './pages/courses/CourseUpkeepCheckout';
import CourseUpkeepCallback from './pages/courses/CourseUpkeepCallback';
import Walls from './pages/Walls';
import WallDetail from './pages/WallDetail';
import Reservables from './pages/Reservables';
import ReservationDetail from './pages/ReservationDetail';
import MyReservations from './pages/MyReservations';

import AdminProfile from './pages/admin/AdminProfile';
import AdminPage from './pages/admin/AdminPage';
import Communications from './pages/admin/Communications';
import Reports from './pages/admin/Reports';
import ReservationsManagement from './pages/admin/ReservationsManagement';
import Finance from './pages/admin/Finance';
import UserDetailsPage from './pages/user-details/UserDetailsPage';
import TeacherDetailsPage from './pages/teacher-details/TeacherDetailsPage';

// import Teacher from './pages/Teacher';
import TeacherProfile from './pages/teacher/TeacherProfile';
import TeacherPage from './pages/teacher/TeacherPage';
import TeacherCourse from './pages/teacher/TeacherCourse';

// import Memberships from './pages/Memberships';
import TestDataGrid from './pages/TestDataGrid';
import Tutorial from './pages/Tutorial';
import Footer from './components/Footer';

// Loading component
// const LoadingScreen = () => (
//   <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
//     <CircularProgress size={60} />
//   </Box>
// );

// Profile wrapper component that shows AdminProfile for admins and Profile for others
const ProfileWrapper = () => {
    const { user } = useAuth();

  if (user?.roles?.includes('admin')) {
    return <AdminProfile />;
  }

  if (user?.roles?.includes('teacher')) {
    return <TeacherProfile />;
  }

  return <MemberProfile />;
};

// Main app content
const AppContent = () => {
  const {  isAuthenticated } = useAuth();

  // console.log('route', window.location.href);
  // console.log('isAuthenticated', isAuthenticated);
  // console.log('loading', loading);

  // if (loading) {
  //   return <LoadingScreen />;
  // }


  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <DynamicFavicon />
      <OAuthCallbackHandler />
      <Navbar />
      <Container maxWidth="xl" sx={{ mt: 0, mb: 0 }}>
        <Routes>
          {/* PUBLIC ROUTES */}
          <Route path='/' element={<Home />} />
          <Route path='/login' element={isAuthenticated ? <Navigate to='/' replace /> : <Login />} />
          <Route path='/register' element={isAuthenticated ? <Navigate to='/' replace /> : <Register />} />

          <Route path='/verify-email' element={<VerifyEmail />} />
          <Route path='/reset-password' element={<ResetPassword />} />
          <Route path='/forgot-password' element={<ForgotPassword />} />
          <Route path='/registration-success' element={<RegistrationSuccess />} />

          {/* PAGINA DI TEST DATAGRID */}
          <Route path='/test-datagrid' element={<TestDataGrid />} />

          {/* TUTORIAL */}
          <Route path='/tutorial' element={<Tutorial />} />

          {/* PROTECTED ROUTES */}
          <Route
            path='/membership-checkout'
            element={
              <ProtectedRoute requiredRole='member'>
                <MembershipCheckout />
              </ProtectedRoute>
            }
          />

          <Route
            path='/membership-callback'
            element={<MembershipCallback />}
          />

          <Route
            path='/profile'
            element={
              <ProtectedRoute>
                <ProfileWrapper />
              </ProtectedRoute>
            }
          />

          <Route
            path='/trial-lessons'
            element={
              <ProtectedRoute requiredRole='member'>
                <TrialLessons />
              </ProtectedRoute>
            }
          />

          <Route
            path='/courses'
            element={
              <ProtectedRoute requiredRole='member'>
                <Courses />
              </ProtectedRoute>
            }
          />
          <Route
            path='/my-courses'
            element={
              <ProtectedRoute requiredRole='member'>
                <MyCourses />
              </ProtectedRoute>
            }
          />

          <Route
            path='/member-calendar'
            element={
              <ProtectedRoute requiredRole='member'>
                <MemberCalendar />
              </ProtectedRoute>
            }
          />

          <Route
            path='/walls'
            element={
              // <ProtectedRoute requiredRole='member'>
                <Walls />
              /* </ProtectedRoute> */
            }
          />

          <Route
            path='/walls/:id'
            element={
              // <ProtectedRoute requiredRole='member'>
                <WallDetail />
              // </ProtectedRoute>
            }
          />

          <Route
            path='/reservables'
            element={
              <ProtectedRoute requiredRole='member'>
                <Reservables />
              </ProtectedRoute>
            }
          />

          <Route
            path='/reservables/:id'
            element={
              <ProtectedRoute requiredRole='member'>
                <ReservationDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path='/my-reservations'
            element={
              <ProtectedRoute requiredRole='member'>
                <MyReservations />
              </ProtectedRoute>
            }
          />

          <Route
            path='/course-enrollment-checkout'
            element={
              <ProtectedRoute requiredRole='member'>
                <CourseEnrollmentCheckout />
              </ProtectedRoute>
            }
          />

          <Route
            path='/course-enrollment-callback'
            element={<CourseEnrollmentCallback />}
          />

          <Route
            path='/course-upkeep-checkout'
            element={
              <ProtectedRoute requiredRole='member'>
                <CourseUpkeepCheckout />
              </ProtectedRoute>
            }
          />

          <Route
            path='/course-upkeep-callback'
            element={<CourseUpkeepCallback />}
          />

          <Route
            path='/admin'
            element={
              <ProtectedRoute requiredRole='admin'>
                <AdminProvider>
                  <AdminPage />
                </AdminProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path='/admin/communications'
            element={
              <ProtectedRoute requiredRole={['admin', 'teacher']}>
                <Communications />
              </ProtectedRoute>
            }
          />

          <Route
            path='/admin/reports'
            element={
              <ProtectedRoute requiredRole='admin'>
                <AdminProvider>
                  <Reports />
                </AdminProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path='/admin/finance'
            element={
              <ProtectedRoute requiredRole='admin'>
                <Finance />
              </ProtectedRoute>
            }
          />

          <Route
            path='/manage-reservations'
            element={
              <ProtectedRoute requiredRole='admin'>
                <ReservationsManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path='/teacher'
            element={
              <ProtectedRoute requiredRole='teacher'>
                <TeacherProvider>
                  <TeacherPage />
                </TeacherProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path='/teacher/course/:id'
            element={
              <ProtectedRoute requiredRole={['teacher', 'admin']}>
                <TeacherProvider>
                  <TeacherCourse />
                </TeacherProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path='/user-details/:userId'
            element={
              <ProtectedRoute requiredRole={['admin', 'teacher']}>
                <UserDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path='/teacher-details/:teacherId'
            element={
              <ProtectedRoute requiredRole={['admin', 'teacher']}>
                <TeacherDetailsPage />
              </ProtectedRoute>
            }
          />

          {/* Catch all route */}
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
      </Container>
      <Footer />
    </Router>
  );
};

// Main App component
const App = () => {
  const config = getConfig();
  
  // PayPal configuration solo se abilitato
  const paypalOptions = config.USE_PAYPAL ? {
    "client-id": config.PAYPAL_CLIENT_ID,
    currency: "EUR",
    intent: "capture",
    "enable-funding": "paypal",
    "disable-funding": "card,venmo,sepa,sofort,ideal,bancontact,giropay"
  } : null;

  // console.log('App Config:', {
  //   USE_PAYPAL: config.USE_PAYPAL,
  //   PAYPAL_CLIENT_ID: config.PAYPAL_CLIENT_ID,
  //   paypalOptions
  // });

  // Se PayPal non è abilitato, non mostrare PayPalScriptProvider
  if (!config.USE_PAYPAL) {
    return (
      <ThemeProvider theme={getTheme()}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='it'>
          <CssBaseline />
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='it'>
        <CssBaseline />
        <PayPalScriptProvider options={paypalOptions}>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </PayPalScriptProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
